import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Alert, Button, Card, Input, Select, Space, Typography } from 'antd';
import { Wrapper02 } from '../../designComponents/wrappers/FlexMainWrapper';
import MainPageTitle from '../../designComponents/titles/MainPageTitle';
import { searchUsers, testRecommendPrompt } from '../../api/main/recommend/recommendPromptRequest';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const RecommendPromptTest = () => {
    const [requestUser, setRequestUser] = useState(null);
    const [targetUser, setTargetUser] = useState(null);
    const [prompt, setPrompt] = useState('');
    const [requestOptions, setRequestOptions] = useState([]);
    const [targetOptions, setTargetOptions] = useState([]);
    const [loadingRequestOptions, setLoadingRequestOptions] = useState(false);
    const [loadingTargetOptions, setLoadingTargetOptions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [responseData, setResponseData] = useState(null);

    const canSubmit = useMemo(() => {
        return Boolean(requestUser && targetUser && prompt.trim());
    }, [requestUser, targetUser, prompt]);

    const mapUserOptions = (users) => {
        return users.map((user) => ({
            label: `${user.name || '-'} / ${user.nickname || '-'} / ${user.id}`,
            value: user.id,
            raw: user,
        }));
    };

    const handleSearchRequestUser = async (keyword) => {
        if (!keyword || keyword.trim().length < 2) {
            setRequestOptions([]);
            return;
        }

        setLoadingRequestOptions(true);
        const users = await searchUsers(keyword.trim());
        setRequestOptions(mapUserOptions(users));
        setLoadingRequestOptions(false);
    };

    const handleSearchTargetUser = async (keyword) => {
        if (!keyword || keyword.trim().length < 2) {
            setTargetOptions([]);
            return;
        }

        setLoadingTargetOptions(true);
        const users = await searchUsers(keyword.trim());
        setTargetOptions(mapUserOptions(users));
        setLoadingTargetOptions(false);
    };

    const onSubmit = async () => {
        setErrorMessage('');
        setResponseData(null);
        setSubmitting(true);

        const payload = {
            user_a_id: requestUser?.value,
            user_b_id: targetUser?.value,
            prompt,
        };

        const result = await testRecommendPrompt(payload);

        if (result?.ok === false) {
            setErrorMessage(result.data || '요청 중 오류가 발생했습니다.');
            setSubmitting(false);
            return;
        }

        setResponseData(result);
        setSubmitting(false);
    };

    return (
        <Wrapper02>
            <MainPageTitle text={'추천 문구 프롬프트 테스트'} />

            <Card style={{ width: '95%', marginTop: 24 }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Section>
                        <Label>피추천자 유저 선택</Label>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="전화번호로 검색 (2자 이상)"
                            filterOption={false}
                            onSearch={handleSearchRequestUser}
                            options={requestOptions}
                            onChange={(value, option) => setRequestUser(option)}
                            loading={loadingRequestOptions}
                            allowClear
                            onClear={() => setRequestUser(null)}
                        />
                    </Section>

                    <Section>
                        <Label>추천 대상 유저 선택</Label>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="전화번호로 검색 (2자 이상)"
                            filterOption={false}
                            onSearch={handleSearchTargetUser}
                            options={targetOptions}
                            onChange={(value, option) => setTargetUser(option)}
                            loading={loadingTargetOptions}
                            allowClear
                            onClear={() => setTargetUser(null)}
                        />
                    </Section>

                    <Section>
                        <Label>프롬프트 입력</Label>
                        <TextArea rows={6} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="추천 문구 생성 프롬프트를 입력하세요." />
                    </Section>

                    <Button type="primary" onClick={onSubmit} disabled={!canSubmit} loading={submitting}>
                        Gemini 프롬프트 테스트 실행
                    </Button>

                    {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

                    {responseData && (
                        <ResultBox>
                            <Text strong>결과</Text>
                            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2)}</Paragraph>
                        </ResultBox>
                    )}
                </Space>
            </Card>
        </Wrapper02>
    );
};

export default RecommendPromptTest;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.div`
    font-size: 14px;
    font-weight: 600;
`;

const ResultBox = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px;
    background: #f8fafc;
`;
